import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Grid, Stack, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import React, { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { save } from '../../api/ventura/product';
import { API_AD_GROUP_PRODUCT_IMAGE_UPLOAD_URL } from '../../constants/apiUrls';
import { useConfig } from '../../hooks/useConfig';
import useConfirmNavChange from '../../hooks/useConfirmNavChange';
import { useConstants } from '../../hooks/useConstants';
import { useSnackBar } from '../../hooks/useSnackBar';
import { camelCaseKeys, snakeCaseKeys } from '../../util/object';
import { uploadFile } from '../../util/services';
import ProductSchema from '../../validationSchemas/ProductSchema';
import { AssociationType } from '../Creatives/CreativesForm';
import CreativesList from '../Creatives/CreativesList';
import Checkbox from '../forms/Checkbox';
import FormEndOptions from '../forms/FormEndOptions';
import FormSection from '../forms/FormSection';
import FormSubsection from '../forms/FormSubsection';
import Select from '../forms/Select';
import TextField from '../forms/TextField';
import Upload from '../forms/Upload';

interface ProductsFormProps {
  product: z.infer<typeof ProductSchema>;
  contractId: number;
  advertiserId: number;
  adGroupId: number;
}

const normalizeFormToProduct = (form) => ({
  ...form,
  alcoholTypeId: form.alcoholTypeId >= 0 ? form.alcoholTypeId : undefined,
});

function ProductsForm({ product, contractId, advertiserId, adGroupId }: ProductsFormProps) {
  const { apiHost } = useConfig();
  const navigate = useNavigate();
  const { constants } = useConstants();
  const { showSnackBar } = useSnackBar();

  const isEdit = get(product, 'productId', false);

  const CATEGORY_LIST = get(constants, 'INHAND.CATEGORY_LIST', []);
  const ALCOHOL_TARGETING = [
    {
      value: -1,
      label: 'None',
    },
    ...get(constants, 'INHAND.ALCOHOL_TARGETING', []),
  ];
  const UPC_SENSITIVITY = get(constants, 'INHAND.UPC_SENSITIVITY', []);
  const IMAGE_BASE = get(constants, 'INHAND.IMAGE_BASE');

  const form = useForm<z.infer<typeof ProductSchema>>({
    resolver: zodResolver(ProductSchema),
  });

  const {
    handleSubmit,
    reset,
    setValue,
    watch,
    trigger,
    formState: { isDirty },
  } = form;
  const defaultValues = useMemo(
    () => ({
      bidderContractId: contractId,
      bidderCampaignId: adGroupId,
      venturaAdvertiserId: advertiserId,
      minAge: 0,
      alcoholTypeId: -1,
    }),
    [contractId, adGroupId, advertiserId]
  );

  useConfirmNavChange(() => isDirty);

  useEffect(() => {
    if (!isEmpty(product)) {
      reset({
        ...defaultValues,
        ...product,
      });
    } else {
      reset(defaultValues);
    }
  }, [reset, product, contractId, advertiserId, adGroupId, defaultValues]);

  const productMutation = useMutation({
    mutationFn: (data) => {
      const payload = snakeCaseKeys(normalizeFormToProduct(data));
      return save(apiHost, adGroupId, { product: payload });
    },
    onSuccess: (data) => {
      if (!data.errors) {
        const productId = get(data, 'productId') || get(data, 'id');
        showSnackBar('Product saved successfully!', 'success', {
          event: isEdit ? 'product_updated' : 'product_created',
          product_id: productId,
          ad_group_id: adGroupId,
        });
        reset({
          ...defaultValues,
          ...camelCaseKeys(data),
          productId: undefined,
        });
      } else {
        showSnackBar(data.errors, 'error', { event: 'product_save_error' });
      }
    },
    onError: (error: Error) => {
      const errorObject = JSON.parse(error.message);
      showSnackBar(errorObject.errors, 'error', { event: 'product_save_error' });
    },
  });

  const { isLoading: isUploading } = productMutation;

  const urlImageMutation = useMutation({
    mutationFn: (files) => {
      const file = files[0];
      return uploadFile(apiHost, API_AD_GROUP_PRODUCT_IMAGE_UPLOAD_URL, { key: file.name }, file);
    },
    onSuccess: (response: any) => {
      setValue('urlImage', response.url_image);
      trigger('urlImage');
      showSnackBar('Product image uploaded successfully!', 'success', {
        event: 'product_image_uploaded',
        ad_group_id: adGroupId,
      });
    },
    onError: (error: Error) => {
      const errorObject = JSON.parse(error.message);
      showSnackBar(errorObject.errors, 'error', { event: 'product_image_upload_error' });
    },
  });

  const setUrlImage = (files) => {
    urlImageMutation.mutate(files);
  };

  const onSubmit = (data) => {
    productMutation.mutate(data);
  };

  const onRemove = () => {
    setValue('urlImage', null);
  };

  const urlImage = watch('urlImage');

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack direction="row" px={2} pt={2}>
          <Typography variant="h5">{isEdit ? 'Edit' : 'Create'} Product</Typography>
        </Stack>
        <Grid container>
          <FormSection title="General">
            <FormSubsection>
              <Stack spacing={2}>
                <TextField name="name" label="Product Name" />
                <TextField name="descr" label="Description" />
                <Select name="catId" label="Category" options={CATEGORY_LIST} />
                <TextField name="upcCodes" label="UPC Codes" />
                <Select name="prefixLen" label="UPC Sensitivity" options={UPC_SENSITIVITY} />
                <Select name="alcoholTypeId" label="Alcohol Targeting" options={ALCOHOL_TARGETING} />
                <Checkbox
                  name="minAge"
                  label="Age 21+ ?"
                  normalizeOutput={(e) => (e.target.checked ? 21 : 0)}
                  normalizeInput={(value) => Boolean(value)}
                />
                <Typography>Product Image</Typography>
                <Stack spacing={2}>
                  {urlImage && (
                    <>
                      <a href={`${IMAGE_BASE}${urlImage}`} rel="noreferrer" target="_blank">
                        <img
                          src={`${IMAGE_BASE}${urlImage}`}
                          alt={`${IMAGE_BASE}${urlImage}`}
                          style={{ maxHeight: '200px' }}
                        />
                      </a>
                      <Stack direction="row">
                        <Button onClick={onRemove}>Remove</Button>
                      </Stack>
                    </>
                  )}
                  {!urlImage && (
                    <Upload
                      accept=".png,.jpg,.jpeg,.gif"
                      name="urlImage"
                      onChange={setUrlImage}
                      rules={{ required: { value: true, message: 'IO file is required' } }}
                      loading={isUploading}
                    />
                  )}
                </Stack>
              </Stack>
            </FormSubsection>
          </FormSection>
          <FormSection>
            <FormSubsection>
              <CreativesList
                advertiserId={advertiserId}
                adGroupId={adGroupId}
                productId={product?.productId}
                associationType={AssociationType.Product}
              />
            </FormSubsection>
          </FormSection>
        </Grid>
        <FormEndOptions position="fixed">
          <Button variant="outlined" onClick={() => navigate(-1)}>
            Back
          </Button>
          <Button type="submit" variant="contained" color="primary" disabled={!isDirty}>
            {isEdit ? 'Save' : 'Create Product'}
          </Button>
        </FormEndOptions>
      </form>
    </FormProvider>
  );
}

export default ProductsForm;
